import { HUB_ENDPOINTS, PROD_HUB_BASE_URL } from "@memorang/constants/urls";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
import {
	authHeaders,
	useAuthTokenGetter,
} from "../../contexts/AuthTokenContext";

const generateDefaultWavePattern = (barCount: number) =>
	Array.from({ length: barCount }, (_, index) => {
		const normalizedIndex = index / barCount;
		const primary = Math.sin(normalizedIndex * Math.PI * 4);
		const secondary = Math.sin(normalizedIndex * Math.PI * 8) * 0.5;
		return (primary + secondary + 2) / 4;
	});

const interpolatePoints = (history: number[], barCount: number) => {
	const result = new Array<number>(barCount);
	const originalLength = history.length;

	for (let i = 0; i < barCount; i++) {
		const position = (i * (originalLength - 1)) / (barCount - 1);
		const lowerIndex = Math.floor(position);
		const upperIndex = Math.min(lowerIndex + 1, originalLength - 1);

		if (lowerIndex === upperIndex) {
			result[i] = history[lowerIndex];
		} else {
			const fraction = position - lowerIndex;
			result[i] =
				history[lowerIndex] +
				fraction * (history[upperIndex] - history[lowerIndex]);
		}
	}

	return result;
};

export type AmplitudeMode = "default" | "recorder";

const buildRecorderAmplitudes = (
	meterHistory: number[],
	barCount: number,
	recordingProgress: number,
): number[] => {
	if (!meterHistory.length || barCount === 0) {
		return Array.from({ length: barCount }, () => 0);
	}

	const clampedProgress = Math.max(0, Math.min(1, recordingProgress));
	const rawFilledBars = Math.round(clampedProgress * barCount);
	const filledBars =
		clampedProgress <= 0 ? 0 : Math.min(barCount, Math.max(1, rawFilledBars));

	if (filledBars === 0) {
		return Array.from({ length: barCount }, () => 0);
	}

	const resampled =
		filledBars === 1
			? [meterHistory[meterHistory.length - 1]]
			: interpolatePoints(meterHistory, filledBars);
	const padCount = barCount - filledBars;
	return [...Array.from({ length: padCount }, () => 0), ...resampled];
};

export const useAmplitudes = ({
	barCount,
	meterHistory,
	mode,
	uri,
	recordingProgress = 1,
	onAmplitudesLoading,
}: {
	barCount: number;
	meterHistory: number[];
	mode: AmplitudeMode;
	uri?: string;
	recordingProgress?: number;
	onAmplitudesLoading?: (isLoading: boolean) => void;
}) => {
	const getAuthToken = useAuthTokenGetter();
	const { data, isLoading, isError } = useQuery<{ waveform: number[] }>({
		queryKey: ["amplitudes", uri, barCount, mode],
		queryFn: async () => {
			const token = await getAuthToken();
			const response = await fetch(
				`${PROD_HUB_BASE_URL}${HUB_ENDPOINTS.GET_WAVEFORM_DATA}?url=${encodeURIComponent(uri ?? "")}&barCount=${barCount}`,
				{
					method: "GET",
					headers: authHeaders(token),
				},
			);
			if (!response.ok) {
				throw new Error("Failed to fetch amplitudes data");
			}
			return response.json();
		},
		enabled: !!uri && barCount > 0 && mode === "default",
		staleTime: Number.POSITIVE_INFINITY,
	});

	useEffect(() => {
		if (onAmplitudesLoading) {
			onAmplitudesLoading(isLoading);
		}
	}, [isLoading, onAmplitudesLoading]);

	return useMemo(() => {
		if (mode === "recorder") {
			return {
				amplitudes: buildRecorderAmplitudes(
					meterHistory,
					barCount,
					recordingProgress,
				),
				isLoading: false,
			};
		}

		if (meterHistory.length) {
			return {
				amplitudes: interpolatePoints(meterHistory, barCount),
				isLoading: false,
			};
		}

		if (isError) {
			return {
				amplitudes: generateDefaultWavePattern(barCount),
				isLoading: false,
			};
		}

		return {
			amplitudes: data?.waveform?.length
				? data.waveform
				: Array.from({ length: barCount }, () => 0),
			isLoading,
		};
	}, [
		barCount,
		meterHistory,
		mode,
		recordingProgress,
		data,
		isLoading,
		isError,
	]);
};
